import * as Sentry from '@sentry/react'
import { SignOutButton } from '@clerk/clerk-react'
import { AlertCircle, Mail, UserX } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ConnectionProblemPage } from './ConnectionProblemPage'
import { isReportSignInFormOpen, ReportSignInIssue } from './ReportSignInIssue'

type Props = {
  readonly status: 'not_found' | 'inactive' | 'deactivated' | 'organization_deactivated' | 'network_error'
  readonly onRetry?: () => Promise<unknown>
  readonly userEmail?: string
}

const STATUS_CONFIG = {
  not_found: { icon: UserX, iconBgColor: 'bg-amber-100', iconColor: 'text-amber-600' },
  inactive: { icon: AlertCircle, iconBgColor: 'bg-orange-100', iconColor: 'text-orange-600' },
  deactivated: { icon: UserX, iconBgColor: 'bg-red-100', iconColor: 'text-red-600' },
  organization_deactivated: { icon: AlertCircle, iconBgColor: 'bg-red-100', iconColor: 'text-red-600' },
} as const

export function AccountStatusPage({ status, onRetry, userEmail }: Props) {
  const { t } = useTranslation()
  const [sentryEventId, setSentryEventId] = useState<string | undefined>()
  const [isReportingIssue, setIsReportingIssue] = useState(isReportSignInFormOpen)

  useEffect(() => {
    // Dedupe per status for the lifetime of the tab so a stuck user reloading
    // doesn't burn a Sentry event each time. Mirrors AuthError.tsx.
    const cacheKey = `account_status_event:${status}`
    const cached = sessionStorage.getItem(cacheKey)
    if (cached) {
      setSentryEventId(cached)
      return
    }
    Sentry.withScope((scope) => {
      scope.setTag('error_type', status === 'network_error' ? 'network_error' : 'account_blocked')
      scope.setTag('account_status', status)
      scope.setLevel(status === 'network_error' ? 'warning' : 'error')
      const eventId = Sentry.captureMessage(`Sign-in blocked: account status "${status}"`)
      if (eventId) {
        sessionStorage.setItem(cacheKey, eventId)
        setSentryEventId(eventId)
      }
    })
  }, [status])

  if (status === 'network_error') {
    return (
      <ConnectionProblemPage
        subtitle={t('connectionProblem.subtitleApi')}
        onRetry={onRetry ?? (() => {})}
        errorContext={{
          error_type: 'network_error',
          url: window.location.href,
          user_agent: navigator.userAgent,
          sentry_event_id: sentryEventId,
        }}
        prefillEmail={userEmail}
      />
    )
  }

  const { icon: Icon, iconBgColor, iconColor } = STATUS_CONFIG[status]

  return (
    <div className='flex min-h-screen items-center justify-center bg-linear-to-br from-blue-50 to-slate-100 p-4'>
      <div className='w-full max-w-lg'>
        <div className='mb-6 flex justify-center'>
          <img src='/engen-logo.png' alt='EnGen' className='h-8' />
        </div>
        <div className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl'>
          {/* Header with icon */}
          <div className='flex flex-col items-center px-8 pt-10 pb-6'>
            <div className={`mb-6 rounded-full ${iconBgColor} p-4`}>
              <Icon className={`h-10 w-10 ${iconColor}`} strokeWidth={1.5} />
            </div>
            <h1 className='text-center text-2xl font-semibold tracking-tight text-slate-900'>
              {t(`accountStatus.${status}.title`)}
            </h1>
          </div>

          {/* Body */}
          <div className='px-8 pb-6'>
            <p className='text-center leading-relaxed text-slate-600'>{t(`accountStatus.${status}.description`)}</p>
          </div>

          {/* Contact info card */}
          <div className='mx-8 mb-6 rounded-xl border border-slate-100 bg-slate-50 p-4'>
            <div className='flex items-start gap-3'>
              <Mail className='mt-0.5 h-5 w-5 shrink-0 text-slate-400' />
              <div>
                <p className='text-sm font-medium text-slate-700'>{t('accountStatus.needHelp')}</p>
                <p className='mt-1 text-sm text-slate-500'>{t('accountStatus.contactAdmin')}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className='space-y-3 border-t border-slate-100 bg-slate-50/50 px-8 py-6'>
            {!isReportingIssue && (
              <SignOutButton>
                <button
                  type='button'
                  className='w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-slate-800'
                >
                  {t('accountStatus.signOut')}
                </button>
              </SignOutButton>
            )}
            <ReportSignInIssue
              variant='link'
              onOpenChange={setIsReportingIssue}
              errorContext={{
                error_type: 'account_blocked',
                account_status: status,
                url: window.location.href,
                user_agent: navigator.userAgent,
                sentry_event_id: sentryEventId,
              }}
              prefillEmail={userEmail}
              prefillEmailVerified={!!userEmail}
            />
          </div>
        </div>

        {/* Footer */}
        <p className='mt-6 text-center text-xs text-slate-400'>{t('accountStatus.footer')}</p>
      </div>
    </div>
  )
}
